import "./EffortUpgradeCard.css";
import { Button } from "@ui/Button";
import { Card } from "@ui/Card";
import { Loader } from "@ui/Loader";
import { toast } from "@ui/molecules/Toasts/toast";
import {
	FALLBACK_QUESTION_EFFORTS,
	loadQuestionEfforts,
} from "@ui/organisms/QuestionBox/questionEfforts";
import { HStack, VStack } from "@ui/Stack";
import { Heading } from "@ui/typography/Heading";
import { Text } from "@ui/typography/Text";
import {
	createEffect,
	createResource,
	createSignal,
	onCleanup,
	onMount,
	Show,
} from "solid-js";
import { respondToQuestionInputRequest } from "@/client";
import { defaultButtonLeads } from "~/modules/shared/utils/platform";
import type {
	EffortUpgradeRequestState,
	EffortUpgradeResolutionState,
} from "../chatPanelState.ts";

const FADE_OUT_MS = 250;

const useEffortName = () => {
	const [efforts] = createResource(() => loadQuestionEfforts(), {
		initialValue: FALLBACK_QUESTION_EFFORTS,
	});
	return (id: string) =>
		efforts().find((effort) => effort.id === id)?.display_name ?? id;
};

type Props = {
	questionId: string;
	interactionId: string;
	request: EffortUpgradeRequestState;
	/** Langchain has settled the request, whichever tab answered it, if any. */
	resolved: boolean;
	/** The answer has moved on without the request being settled, e.g. it failed. */
	settled: boolean;
};

/**
 * Asking, then waiting for langchain to confirm the outcome on the stream, then
 * fading out. The countdown running out waits too: langchain still accepts an
 * answer for a short grace period after `respond_by`.
 */
type Phase = "asking" | "waiting" | "closing" | "gone";

/**
 * Should the confirmation never arrive (the stream dropped, the worker died),
 * the card gives up this long after `respond_by` rather than spin forever.
 */
const GIVE_UP_AFTER_RESPOND_BY_MS = 15_000;

/** Asks whether the answer may go deeper than the effort it was asked at. */
export const EffortUpgradeCard = (props: Props) => {
	const effortName = useEffortName();
	const [now, setNow] = createSignal(Date.now());
	const tick = setInterval(() => setNow(Date.now()), 250);
	onCleanup(() => clearInterval(tick));

	const secondsLeft = () =>
		Math.max(0, Math.ceil((props.request.respondByMs - now()) / 1000));

	const giveUpInMs =
		props.request.respondByMs + GIVE_UP_AFTER_RESPOND_BY_MS - Date.now();
	const [phase, setPhase] = createSignal<Phase>(
		props.request.live && !props.resolved && !props.settled && giveUpInMs > 0
			? "asking"
			: "gone",
	);
	/** The user's choice, or undefined when the countdown ran out first. */
	const [accepted, setAccepted] = createSignal<boolean>();
	let cardEl: HTMLDivElement | undefined;
	let yesButton: HTMLButtonElement | undefined;

	const close = () => {
		if (phase() === "closing" || phase() === "gone") {
			return;
		}
		setPhase("closing");
		clearInterval(tick);
		const fade = cardEl?.animate([{ opacity: 1 }, { opacity: 0 }], {
			duration: FADE_OUT_MS,
			easing: "ease-out",
			fill: "forwards",
		});
		if (!fade) {
			setPhase("gone");
			return;
		}
		fade.onfinish = () => setPhase("gone");
	};

	if (phase() !== "gone") {
		const giveUp = setTimeout(close, giveUpInMs);
		onCleanup(() => clearTimeout(giveUp));
	}

	const wait = (choice: boolean | undefined) => {
		// The buttons are about to go, and focus with them, so keep it in the card.
		const hadFocus = cardEl?.contains(document.activeElement) ?? false;
		setAccepted(choice);
		setPhase("waiting");
		if (hadFocus) {
			cardEl?.focus({ preventScroll: true });
		}
	};

	const respond = async (choice: boolean) => {
		if (phase() !== "asking") {
			return;
		}
		wait(choice);

		let status: number | undefined;
		try {
			const result = await respondToQuestionInputRequest({
				path: {
					question_id: props.questionId,
					interaction_id: props.interactionId,
					request_id: props.request.requestId,
				},
				body: { type: "effort_upgrade", accepted: choice },
			});
			if (!result.error) {
				return;
			}
			status = result.response?.status;
		} catch {}

		// Already answered (another tab) or expired: the outcome is on its way
		// down the stream either way, so there is nothing to put right.
		if (status === 409 || status === 412 || phase() !== "waiting") {
			return;
		}
		toast.error({ title: "Couldn't send your choice" });
		if (secondsLeft() > 0) {
			setAccepted(undefined);
			setPhase("asking");
		}
	};

	createEffect(() => {
		if (props.resolved || props.settled) {
			close();
		} else if (phase() === "asking" && secondsLeft() === 0) {
			wait(undefined);
		}
	});

	// Being the default, Yes takes Enter, but only when that steals nothing.
	onMount(() => {
		if (!document.activeElement || document.activeElement === document.body) {
			yesButton?.focus({ preventScroll: true });
		}
	});

	const yes = (
		<Button ref={yesButton} variant="primary" onClick={() => respond(true)}>
			Yes
		</Button>
	);
	const no = (
		<Button variant="secondary" theme="grey" onClick={() => respond(false)}>
			No
		</Button>
	);

	return (
		<Show when={phase() !== "gone"}>
			<div ref={cardEl} class="effort-upgrade-card" tabIndex={-1}>
				<Card>
					<VStack spacing="large">
						<VStack spacing="small">
							<Heading as="h4">Effort upgrade</Heading>
							<Text>
								A higher effort level is required to answer this question fully.
								Can I switch to {effortName(props.request.suggestedEffort)}{" "}
								level?
							</Text>
						</VStack>
						<HStack spacing="medium" align="center" justify="space-between">
							<Show
								when={phase() === "asking"}
								fallback={
									<>
										<Text size="small" color="fg3">
											{accepted()
												? `Switching to ${effortName(props.request.suggestedEffort)}…`
												: `Continuing at ${effortName(props.request.defaultEffort)}…`}
										</Text>
										<Loader size="small" />
									</>
								}
							>
								<Text size="small" color="fg3">
									Continuing at {effortName(props.request.defaultEffort)} in{" "}
									{secondsLeft()}s
								</Text>
								<HStack spacing="medium">
									<Show
										when={defaultButtonLeads()}
										fallback={
											<>
												{no}
												{yes}
											</>
										}
									>
										{yes}
										{no}
									</Show>
								</HStack>
							</Show>
						</HStack>
					</VStack>
				</Card>
			</div>
		</Show>
	);
};

/** What became of an effort upgrade request, once langchain has settled it. */
export const EffortUpgradeResolution = (props: {
	resolution: EffortUpgradeResolutionState;
	request?: EffortUpgradeRequestState;
}) => {
	const effortName = useEffortName();
	const upgraded = () =>
		props.request !== undefined &&
		props.resolution.resolvedEffort !== props.request.defaultEffort;

	// Live, it closes the question the card just asked. In history there was no
	// card, so an effort left as asked is nothing worth reporting.
	return (
		<Show when={props.resolution.live || upgraded()}>
			<Text family="mono" size="small" color="fg3">
				{upgraded() ? "Switched to" : "Continued at"}{" "}
				{effortName(props.resolution.resolvedEffort)} level
			</Text>
		</Show>
	);
};
